import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import { ChevronLeft, ChevronRight, ArrowLeft, Lock, Loader2, Printer, BookOpen, Volume2, VolumeX } from "lucide-react";

const FILES = `${process.env.REACT_APP_BACKEND_URL}/api/files/`;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function buildPages(data, lang) {
  const a = data.vachana.assets || {};
  const pages = [];
  if (a.title_page) pages.push({ key: "title", label: "Title", image: a.title_page });
  const summary = lang === "kn" ? a.kannada_summary : a.english_summary;
  if (summary) pages.push({ key: "summary", label: lang === "kn" ? "ಸಾರಾಂಶ" : "Summary", image: summary });
  data.panels.forEach((p) => {
    const img = lang === "kn" ? p.kannada_image : p.english_image;
    if (img) pages.push({ key: `panel-${p.panel_id}`, label: `Panel ${p.panel_number}`, image: img, audio: p.audio });
  });
  if (a.color_reference) pages.push({ key: "colorref", label: "Color Reference", image: a.color_reference });
  if (a.colouring_page) pages.push({ key: "colouring", label: "Colouring Page", image: a.colouring_page });
  return pages;
}

export default function EComicsReader() {
  const { volumeId, vachanaId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [locked, setLocked] = useState(false);
  const [error, setError] = useState("");
  const [lang, setLang] = useState("kn");
  const [idx, setIdx] = useState(0);
  const [ownedVols, setOwnedVols] = useState([]);
  const [autoplay, setAutoplay] = useState(() => localStorage.getItem("ec_autoplay") === "1");
  const touchX = useRef(null);
  const restoredRef = useRef(false);
  const saveTimer = useRef(null);
  const celebratedRef = useRef(false);

  const toggleAutoplay = () => {
    setAutoplay(a => {
      localStorage.setItem("ec_autoplay", a ? "0" : "1");
      return !a;
    });
  };

  useEffect(() => {
    Promise.all([
      fetch(`${API}/ecomics/auth/me`, { credentials: "include" }).then(r => r.json()).catch(() => ({ purchased_volume_ids: [] })),
      fetch(`${API}/ecomics/volumes`).then(r => r.json()).catch(() => []),
    ]).then(([me, vols]) => {
      const ids = me.purchased_volume_ids || [];
      setOwnedVols(vols.filter(v => ids.includes(v.volume_id)));
    });
  }, []);

  useEffect(() => {
    setData(null); setLocked(false); setError(""); setIdx(0);
    restoredRef.current = false;
    celebratedRef.current = false;
    fetch(`${API}/ecomics/volumes/${volumeId}/read/${vachanaId}`, { credentials: "include" })
      .then(async (r) => {
        if (r.status === 402) { setLocked(true); return null; }
        if (!r.ok) { setError("Content not available."); return null; }
        return r.json();
      })
      .then((d) => { if (d) setData(d); })
      .catch(() => setError("Content not available."));
  }, [volumeId, vachanaId]);

  const pages = useMemo(() => (data ? buildPages(data, lang) : []), [data, lang]);
  const page = pages[Math.min(idx, Math.max(pages.length - 1, 0))];

  // Resume reading: server progress (signed-in) or localStorage (guest)
  useEffect(() => {
    if (!data || pages.length === 0 || restoredRef.current) return;
    restoredRef.current = true;
    let p = data.progress?.last_page_index;
    if (p == null) {
      const ls = localStorage.getItem(`ec_pos_${vachanaId}`);
      p = ls ? parseInt(ls, 10) : 0;
    }
    if (p > 0 && p < pages.length) setIdx(p);
  }, [data, pages.length, vachanaId]);

  // Save progress on page change (debounced server save when signed in)
  useEffect(() => {
    if (!data || pages.length === 0 || !restoredRef.current) return;
    localStorage.setItem(`ec_pos_${vachanaId}`, String(idx));
    if (!data.logged_in) return;
    clearTimeout(saveTimer.current);
    const body = JSON.stringify({
      vachana_id: vachanaId, volume_id: volumeId,
      page_index: idx, total_pages: pages.length,
      finished: idx === pages.length - 1,
    });
    saveTimer.current = setTimeout(() => {
      fetch(`${API}/ecomics/progress`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" }, body,
      }).catch(() => {});
    }, 500);
    return () => clearTimeout(saveTimer.current);
  }, [idx, data, pages.length, vachanaId, volumeId]);

  // Finished celebration: confetti the first time the last page is reached
  useEffect(() => {
    if (!data || pages.length < 2 || !restoredRef.current || celebratedRef.current) return;
    if (idx !== pages.length - 1) return;
    if (data.progress?.finished) return;
    celebratedRef.current = true;
    const burst = (x, angle) => confetti({
      particleCount: 70, spread: 65, angle, origin: { x, y: 0.75 },
      colors: ["#C25946", "#1C3F34", "#E8B84B", "#F7F5F0"], zIndex: 9999,
    });
    burst(0.2, 60);
    burst(0.8, 120);
    setTimeout(() => burst(0.5, 90), 350);
  }, [idx, data, pages.length]);

  const go = useCallback((dir) => {
    setIdx((i) => Math.max(0, Math.min(pages.length - 1, i + dir)));
  }, [pages.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "Escape") nav(`/ecomics/volume/${volumeId}`);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, nav, volumeId]);

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
    touchX.current = null;
  };

  if (locked) {
    return (
      <div className="min-h-screen bg-[#141210] text-white grid place-items-center px-6" data-testid="ecomics-reader-locked">
        <div className="text-center max-w-sm">
          <Lock size={30} className="mx-auto mb-4 text-[#e8a087]"/>
          <h1 className="font-serif text-2xl">This vachana is locked</h1>
          <p className="mt-2 text-sm text-white/60">Buy this volume to read all vachanas. The first vachana is free to preview.</p>
          <Link to={`/ecomics/volume/${volumeId}`} className="mt-6 inline-block px-6 py-2.5 rounded-full bg-[#C25946] text-white font-medium hover:bg-[#a84a3a] transition" data-testid="ecomics-locked-buy-link">
            View volume & buy
          </Link>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-[#141210] text-white grid place-items-center px-6" data-testid="ecomics-reader-error">
        <div className="text-center">
          <p>{error}</p>
          <Link to={`/ecomics/volume/${volumeId}`} className="mt-4 inline-block text-[#e8a087] hover:underline">← Back to volume</Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-[#141210] text-white grid place-items-center" data-testid="ecomics-reader-loading">
        <Loader2 size={26} className="animate-spin text-[#e8a087]"/>
      </div>
    );
  }

  const next = pages[idx + 1];
  const isLast = idx === pages.length - 1 && pages.length > 0;
  const onColouringPage = page?.key === "colouring";
  const sibIdx = data.siblings.findIndex((s) => s.vachana_id === vachanaId);
  const nextVachana = sibIdx >= 0 ? data.siblings[sibIdx + 1] : null;
  const pdfUrl = `${API}/ecomics/volumes/${volumeId}/read/${vachanaId}/colouring.pdf`;

  return (
    <div className="min-h-screen bg-[#141210] text-white flex flex-col select-none" data-testid="ecomics-reader">
      {/* Top bar */}
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-white/10">
        <div className="flex items-center gap-2 min-w-0">
          <Link to={`/ecomics/volume/${volumeId}`} className="inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white transition shrink-0" data-testid="ecomics-reader-back">
            <ArrowLeft size={15}/> <span className="hidden sm:inline">Vol {data.volume.volume_number}</span>
          </Link>
          {ownedVols.length > 0 && (
            <select
              value={volumeId}
              onChange={(e) => {
                const v = ownedVols.find(o => o.volume_id === e.target.value);
                if (!v) return;
                nav(v.first_vachana_id ? `/ecomics/read/${v.volume_id}/${v.first_vachana_id}` : `/ecomics/volume/${v.volume_id}`);
              }}
              className="bg-white/10 border border-white/15 rounded-full text-xs text-white px-2.5 py-1.5 outline-none max-w-[130px] sm:max-w-[180px] truncate cursor-pointer"
              data-testid="ecomics-volume-switcher"
            >
              {!ownedVols.some(o => o.volume_id === volumeId) && (
                <option value={volumeId} className="text-black">Vol {data.volume.volume_number} · {data.volume.title}</option>
              )}
              {ownedVols.map(v => (
                <option key={v.volume_id} value={v.volume_id} className="text-black">Vol {v.volume_number} · {v.title}</option>
              ))}
            </select>
          )}
        </div>
        <div className="min-w-0 text-center">
          <div className="text-sm font-medium truncate" data-testid="ecomics-reader-title">
            {lang === "kn" ? data.vachana.kannada_title : data.vachana.english_title}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-white/40">
            {data.is_free_preview && !data.purchased ? "Free preview · " : ""}{page?.label} · {idx + 1}/{pages.length}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleAutoplay}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs transition ${autoplay ? "bg-[#C25946] border-[#C25946] text-white" : "border-white/15 text-white/60 hover:text-white"}`}
            title="Auto-play panel audio when the page turns"
            data-testid="ecomics-autoplay-toggle"
          >
            {autoplay ? <Volume2 size={13}/> : <VolumeX size={13}/>}
            <span className="hidden sm:inline">Autoplay {autoplay ? "ON" : "OFF"}</span>
          </button>
          <div className="flex rounded-full border border-white/15 overflow-hidden text-xs" data-testid="ecomics-lang-toggle">
            {[["kn", "ಕನ್ನಡ"], ["en", "EN"]].map(([k, l]) => (
              <button key={k} onClick={() => { setLang(k); }} className={`px-3 py-1.5 transition ${lang === k ? "bg-[#C25946] text-white" : "text-white/60 hover:text-white"}`} data-testid={`ecomics-lang-${k}`}>{l}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Page area */}
      <div className="flex-1 relative flex items-center justify-center overflow-hidden" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} data-testid="ecomics-reader-stage">
        {pages.length === 0 ? (
          <div className="text-white/50 text-sm px-6 text-center" data-testid="ecomics-reader-empty">No pages available in this language yet.</div>
        ) : (
          <>
            <img
              key={page.key + lang}
              src={FILES + page.image}
              alt={page.label}
              className="max-h-[calc(100vh-150px)] max-w-full object-contain px-2"
              data-testid="ecomics-reader-image"
            />
            {next?.image && <link rel="preload" as="image" href={FILES + next.image}/>}
            <button onClick={() => go(-1)} disabled={idx === 0} className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 backdrop-blur grid place-items-center disabled:opacity-20 hover:bg-black/60 transition" data-testid="ecomics-reader-prev"><ChevronLeft size={20}/></button>
            <button onClick={() => go(1)} disabled={idx >= pages.length - 1} className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 backdrop-blur grid place-items-center disabled:opacity-20 hover:bg-black/60 transition" data-testid="ecomics-reader-next"><ChevronRight size={20}/></button>
          </>
        )}
      </div>

      {/* Bottom bar: per-panel audio + actions + progress dots */}
      <div className="px-4 py-3 border-t border-white/10 space-y-2">
        {page?.audio && (
          <audio key={page.key + (autoplay ? "-a" : "")} controls autoPlay={autoplay} preload={autoplay ? "auto" : "none"} src={FILES + page.audio} className="w-full max-w-md mx-auto block h-9" data-testid="ecomics-reader-audio"/>
        )}
        {(onColouringPage || isLast) && (
          <div className="flex flex-wrap items-center justify-center gap-3">
            {onColouringPage && (
              <a href={pdfUrl} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-white/25 text-sm text-white/85 hover:bg-white/10 transition" data-testid="ecomics-colouring-pdf-btn">
                <Printer size={14}/> Download colouring PDF
              </a>
            )}
            {isLast && nextVachana && (data.purchased ? (
              <Link to={`/ecomics/read/${volumeId}/${nextVachana.vachana_id}`} className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-[#C25946] text-white text-sm font-medium hover:bg-[#a84a3a] transition" data-testid="ecomics-next-vachana-btn">
                Next vachana: {nextVachana.kannada_title} <ChevronRight size={14}/>
              </Link>
            ) : (
              <Link to={`/ecomics/volume/${volumeId}`} className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-[#C25946] text-white text-sm font-medium hover:bg-[#a84a3a] transition" data-testid="ecomics-unlock-rest-btn">
                <Lock size={13}/> Unlock all vachanas
              </Link>
            ))}
            {isLast && !nextVachana && (
              <Link to={`/ecomics/volume/${volumeId}`} className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full border border-white/25 text-sm text-white/85 hover:bg-white/10 transition" data-testid="ecomics-back-to-volume-btn">
                <BookOpen size={14}/> Back to volume
              </Link>
            )}
          </div>
        )}
        <div className="flex items-center justify-center gap-1.5 flex-wrap" data-testid="ecomics-reader-dots">
          {pages.map((p, i) => (
            <button key={p.key} onClick={() => setIdx(i)} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-5 bg-[#C25946]" : "w-1.5 bg-white/25 hover:bg-white/50"}`} aria-label={p.label}/>
          ))}
        </div>
      </div>
    </div>
  );
}
